import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ProductDetail } from '../../../../types/ProductTypes';
import { useEditMode } from '../../../../context/EditModeContext';
import { useAuthStore } from '../../../../context/AuthContext';
import { useProductEdit } from '../../../../context/ProductEditContext';
import { useUploadFile } from '../../../../api/useUploadFile';
import { useDownloadFile } from '../../../../api/useDownloadFile';
import { useDeleteFile } from '../../../../api/useDeleteFile';
import { useRenameFile } from '../../../../api/useRenameFile';
import { formatMarpolRegulation } from '../../../../utils/regulationFormatters';
import { formatCategoryName } from '../../../../utils/categoryNameFormatter';
import {
    SectionCard,
    SectionTitle,
    InfoGrid,
    InfoItem,
    ProductNameField,
    FullWidthFields,
    FullWidthItem,
    WarningBox,
    DocumentSection,
    DocumentList,
    DocumentItem,
    DocumentSubSection,
    ScrollableContent,
    UploadButton,
    HiddenFileInput,
    DeleteButton,
    EditButton,
    RenameInput,
    RenameActions,
    ConfirmButton,
    CancelButton,
} from '../../Section.styles';
import { EditableInput, EditableTextarea, EditableSelect } from '../../EditableFields.styles';
import { useChartererStore } from '../../../../context/ChartererContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faPen, faCheck, faTimes } from '@fortawesome/free-solid-svg-icons';

interface RelatedProduct {
    id: number;
    categoryName: string;
    tradeName: string | null;
    chartererName: string | null;
    synonyms: string;
    contains?: string | null;
    isActive?: boolean;
}

interface GeneralInformationSectionProps {
    product: ProductDetail;
    relatedProducts: RelatedProduct[];
    onProductUpdated?: () => void;
    isCreatingNewProduct?: boolean;
    isCreatingNewCategory?: boolean;
    /** Category product's Contains value, resolved by the parent (ProductPage). */
    resolvedContains?: string | null;
}

const GeneralInformationSection: React.FC<GeneralInformationSectionProps> = ({ product, relatedProducts, onProductUpdated, isCreatingNewProduct = false, isCreatingNewCategory = false, resolvedContains }) => {
    const { isInEditMode } = useEditMode();
    const { canEdit, canEditChemGuard } = useAuthStore();
    const hasEditorRole = canEdit || canEditChemGuard;
    const productEditContext = useProductEdit();
    const mergePendingChanges = productEditContext?.mergePendingChanges;
    const getPendingChanges = productEditContext?.getPendingChanges;
    const { charterers } = useChartererStore();
    const { uploadFile, isUploading, clearMessages } = useUploadFile();
    const { downloadFile } = useDownloadFile();
    const { deleteFile, isDeleting } = useDeleteFile();
    const { renameFile, isRenaming } = useRenameFile();
    const showEditUI = canEdit && isInEditMode;

    // File upload refs
    const msdsFileInputRef = useRef<HTMLInputElement>(null);
    const productDocFileInputRef = useRef<HTMLInputElement>(null);

    // State for renaming files
    const [renamingFile, setRenamingFile] = useState<{ category: string; index: number; originalName: string } | null>(null);
    const [newFileName, setNewFileName] = useState('');

    // Local document state - tracks uploads/deletes/renames before Save
    const [localMsdsDocs, setLocalMsdsDocs] = useState<any[]>(product.materialSafetyDataSheets || []);
    const [localProductDocs, setLocalProductDocs] = useState<any[]>(product.productDocuments || []);

    // Check if this is a product category (no trade name)
    // When creating a new category, treat as category so Trade Name/Charterer/CAS/Contains are hidden
    // When creating a new charterer-specific product (copy), treat as non-category so those fields are visible
    const isProductCategory = isCreatingNewCategory ? true : (isCreatingNewProduct ? false : !product.tradeName);

    // Use the resolved Contains from the parent (fetched from category product's full detail).
    // Falls back to current product's own Contains if parent didn't provide it.
    const categoryContains = resolvedContains ?? product.regulations?.tripartiteAgreement?.contains ?? null;

    // Helper to extract synonym strings from synonyms (handles both string[] and object[] formats)
    const getSynonymsString = (synonyms: any[] | undefined): string => {
        if (!synonyms || synonyms.length === 0) return '';

        // If synonyms are objects with synonymName property
        if (typeof synonyms[0] === 'object' && synonyms[0] !== null && 'synonymName' in synonyms[0]) {
            return synonyms.map((syn) => syn.synonymName).join(', ');
        }

        // If synonyms are already strings
        return synonyms.join(', ');
    };

    // Form state for editable fields
    const [formData, setFormData] = useState({
        tradeName: product.tradeName || '',
        categoryName: product.category?.name || '',
        chartererName: product.charterer?.id?.toString() || '',
        casNumber: product.externalIds?.find((id) => id.source === 'CAS')?.value || '',
        synonyms: getSynonymsString(product.synonyms),
        contains: product.regulations?.tripartiteAgreement?.contains || '',
        regulation: String(product.category?.regulations?.marpolRegulation || 1),
        description: '',
        warning: '',
        commentsMSG: '',
    });

    // Helper to update form and push changes to context
    const updateFormField = useCallback(
        (field: string, value: string) => {
            setFormData((prev) => ({ ...prev, [field]: value }));

            if (!mergePendingChanges) return;

            // Get current pending changes to properly merge comments
            const currentPendingChanges = getPendingChanges ? getPendingChanges() : {};

            // Push change to context based on field type
            switch (field) {
                case 'tradeName':
                    mergePendingChanges({ tradeName: value });
                    break;
                case 'categoryName': {
                    const currentCat = currentPendingChanges.category || product.category || {};
                    mergePendingChanges({ category: { ...currentCat, name: value } as any });
                    break;
                }
                case 'regulation': {
                    // Update the category's marpol regulation
                    const regValue = parseInt(value) || 0;
                    const currentCat = (currentPendingChanges.category || product.category || {}) as any;
                    // When switching regulation, reset Annex II fields if moving away from Chapter 17/18
                    const newRegs: any = {
                        ...(currentCat.regulations || {}),
                        marpolRegulation: regValue,
                    };
                    // Reset pollution category and IBC fields when switching away from Annex II
                    if (regValue !== 2 && regValue !== 3) {
                        newRegs.marpolPollutionCategory = 0;
                        newRegs.ibcRegulation = null;
                    }
                    if (regValue !== 2) {
                        // Only Chapter 17 needs IBC
                        newRegs.ibcRegulation = null;
                    }
                    mergePendingChanges({
                        category: {
                            ...currentCat,
                            regulations: newRegs,
                        } as any,
                    });
                    break;
                }
                case 'chartererName': {
                    // value is the charterer Id from the dropdown
                    const selectedCharterer = charterers.find((c) => c.id === Number(value));
                    if (selectedCharterer) {
                        mergePendingChanges({ charterer: selectedCharterer });
                    }
                    break;
                }
                case 'casNumber': {
                    const existingCasId = product.externalIds?.find((id) => id.source === 'CAS');
                    const updatedExternalIds = product.externalIds?.map((id) => (id.source === 'CAS' ? { ...id, value } : id)) || [];
                    if (!existingCasId && value) {
                        updatedExternalIds.push({ id: 0, source: 'CAS', value, lastUpdatedUtc: new Date().toISOString() });
                    }
                    mergePendingChanges({ externalIds: updatedExternalIds });
                    break;
                }
                case 'synonyms': {
                    const synonymsArray = value
                        .split(',')
                        .map((s) => s.trim())
                        .filter((s) => s.length > 0)
                        .map((s) => ({ id: 0, synonymName: s }));
                    mergePendingChanges({ synonyms: synonymsArray as any });
                    break;
                }
                case 'contains': {
                    const currentTripartite = currentPendingChanges.regulations?.tripartiteAgreement
                        || product.regulations?.tripartiteAgreement;
                    const baseRegulations = currentPendingChanges.regulations || product.regulations;
                    mergePendingChanges({
                        regulations: {
                            ...baseRegulations,
                            tripartiteAgreement: {
                                ...currentTripartite,
                                contains: value || null,
                            },
                        },
                    } as any);
                    break;
                }
                case 'description': {
                    // Get base comments from pending changes or product
                    const baseComments = currentPendingChanges.comments || product.comments || [];
                    const descComment = product.comments?.find((c) => c.category === 4);
                    // Keep all non-description comments, replace/add description
                    const otherComments = baseComments.filter((c: any) => c.category !== 4);
                    const updatedComments = [
                        ...otherComments,
                        {
                            id: descComment?.id || 0,
                            text: value,
                            category: 4,
                            visibility: 1,
                            lastUpdatedUtc: new Date().toISOString(),
                        },
                    ];
                    mergePendingChanges({ comments: updatedComments });
                    break;
                }
                case 'warning': {
                    // Get base comments from pending changes or product
                    const baseComments = currentPendingChanges.comments || product.comments || [];
                    const warnComment = product.comments?.find((c) => c.category === 5);
                    // Keep all non-warning comments, replace/add warning
                    const otherComments = baseComments.filter((c: any) => c.category !== 5);
                    const updatedComments = value
                        ? [
                              ...otherComments,
                              {
                                  id: warnComment?.id || 0,
                                  text: value,
                                  category: 5,
                                  visibility: 1,
                                  lastUpdatedUtc: new Date().toISOString(),
                              },
                          ]
                        : otherComments;
                    mergePendingChanges({ comments: updatedComments });
                    break;
                }
                case 'commentsMSG': {
                    // Get base comments from pending changes or product
                    const baseComments = currentPendingChanges.comments || product.comments || [];
                    const msgComment = product.comments?.find((c) => c.category === 1);
                    // Keep all non-MSG comments, replace/add MSG comment
                    const otherComments = baseComments.filter((c: any) => c.category !== 1);
                    const updatedComments = value
                        ? [
                              ...otherComments,
                              {
                                  id: msgComment?.id || 0,
                                  text: value,
                                  category: 1,
                                  visibility: 1,
                                  lastUpdatedUtc: new Date().toISOString(),
                              },
                          ]
                        : otherComments;
                    mergePendingChanges({ comments: updatedComments });
                    break;
                }
            }
        },
        [mergePendingChanges, getPendingChanges, product]
    );

    // Initialize form data when product changes (skip during creation — Effect below handles that)
    useEffect(() => {
        if (isCreatingNewProduct) return;

        const warningComment = product.comments?.find((comment) => comment.category === 5);
        const descriptionComment = product.comments?.find((comment) => comment.category === 4);
        const msgComment = product.comments?.find((comment) => comment.category === 1);
        const description = descriptionComment?.text || '';

        setFormData({
            tradeName: product.tradeName || '',
            categoryName: product.category?.name || '',
            chartererName: product.charterer?.id?.toString() || '',
            casNumber: product.externalIds?.find((id) => id.source === 'CAS')?.value || '',
            synonyms: getSynonymsString(product.synonyms),
            contains: product.regulations?.tripartiteAgreement?.contains || '',
            regulation: String(product.category?.regulations?.marpolRegulation || 1),
            description: description,
            warning: warningComment?.text || '',
            commentsMSG: msgComment?.text || '',
        });
        setLocalMsdsDocs(product.materialSafetyDataSheets || []);
        setLocalProductDocs(product.productDocuments || []);
    }, [product, isCreatingNewProduct]);

    // When creating a new product, sync form data from pending changes
    // (the product prop still points to the original category product)
    useEffect(() => {
        if (!isCreatingNewProduct || !getPendingChanges) return;
        const pending = getPendingChanges();
        setFormData((prev) => ({
            ...prev,
            tradeName: pending.tradeName ?? prev.tradeName,
            chartererName: pending.charterer?.id?.toString() || '',
        }));
    }, [isCreatingNewProduct, getPendingChanges]);

    // Handle MSDS file upload
    const handleMsdsFileUpload = useCallback(
        async (event: React.ChangeEvent<HTMLInputElement>) => {
            const file = event.target.files?.[0];
            if (!file) return;

            clearMessages();

            try {
                // 1. Upload file to blob storage
                await uploadFile({
                    category: 'MSDSDocuments',
                    fileName: file.name,
                    file: file,
                });

                // 2. Add document record to local state (shows immediately)
                const newMsds = {
                    id: 0,
                    filePath: file.name,
                    lastUpdatedUtc: new Date().toISOString(),
                    comments: null,
                    receivedDate: null,
                    receivedFrom: null,
                    date: null,
                    version: null,
                };
                setLocalMsdsDocs((prev) => [...prev, newMsds]);
                // 3. Push to pending changes so Save will persist it
                if (mergePendingChanges) {
                    const updated = [...(localMsdsDocs || []), newMsds];
                    console.log('[handleMsdsFileUpload] localMsdsDocs at merge time:', localMsdsDocs);
                    console.log('[handleMsdsFileUpload] updated array for pendingChanges:', updated);
                    mergePendingChanges({ materialSafetyDataSheets: updated } as any);
                }
            } catch (err) {
                console.error('Failed to upload MSDS file:', err);
                alert(`Failed to upload MSDS file: ${err}`);
            }

            // Reset file input
            if (msdsFileInputRef.current) {
                msdsFileInputRef.current.value = '';
            }
        },
        [uploadFile, clearMessages, mergePendingChanges, localMsdsDocs]
    );

    // Handle Product Document file upload
    const handleProductDocFileUpload = useCallback(
        async (event: React.ChangeEvent<HTMLInputElement>) => {
            const file = event.target.files?.[0];
            if (!file) return;

            clearMessages();

            try {
                // 1. Upload file to blob storage
                await uploadFile({
                    category: 'ProductDocuments',
                    fileName: file.name,
                    file: file,
                });

                // 2. Add document record to local state (shows immediately)
                const newDoc = {
                    id: 0,
                    filePath: file.name,
                    lastUpdatedUtc: new Date().toISOString(),
                };
                setLocalProductDocs((prev) => [...prev, newDoc]);
                // 3. Push to pending changes so Save will persist it
                if (mergePendingChanges) {
                    const updated = [...(localProductDocs || []), newDoc];
                    console.log('[handleProductDocFileUpload] localProductDocs at merge time:', localProductDocs);
                    console.log('[handleProductDocFileUpload] updated array for pendingChanges:', updated);
                    mergePendingChanges({ productDocuments: updated } as any);
                }
            } catch (err) {
                console.error('Failed to upload product document:', err);
                alert(`Failed to upload product document: ${err}`);
            }

            // Reset file input
            if (productDocFileInputRef.current) {
                productDocFileInputRef.current.value = '';
            }
        },
        [uploadFile, clearMessages, mergePendingChanges, localProductDocs]
    );

    // Handle file deletion - remove from local list and push to pendingChanges
    const handleDeleteFile = useCallback(
        (category: string, fileName: string) => {
            if (!confirm(`Are you sure you want to delete "${fileName}"?`)) {
                return;
            }

            if (category === 'MSDSDocuments') {
                const updated = localMsdsDocs.filter((msds: any) => msds.filePath !== fileName);
                setLocalMsdsDocs(updated);
                if (mergePendingChanges) {
                    mergePendingChanges({ materialSafetyDataSheets: updated } as any);
                }
            } else if (category === 'ProductDocuments') {
                const updated = localProductDocs.filter((doc: any) => doc.filePath !== fileName);
                setLocalProductDocs(updated);
                if (mergePendingChanges) {
                    mergePendingChanges({ productDocuments: updated } as any);
                }
            }
        },
        [mergePendingChanges, localMsdsDocs, localProductDocs]
    );

    // Handle starting file rename
    const handleStartRename = useCallback((category: string, index: number, originalName: string) => {
        setRenamingFile({ category, index, originalName });
        setNewFileName(originalName);
    }, []);

    // Handle canceling rename
    const handleCancelRename = useCallback(() => {
        setRenamingFile(null);
        setNewFileName('');
    }, []);

    // Handle confirming file rename
    const handleConfirmRename = useCallback(async () => {
        if (!renamingFile || !newFileName.trim()) return;

        if (newFileName === renamingFile.originalName) {
            handleCancelRename();
            return;
        }

        try {
            // 1. Rename file in blob storage
            await renameFile({
                category: renamingFile.category,
                oldFileName: renamingFile.originalName,
                newFileName: newFileName.trim(),
            });

            // 2. Update local state and push to pendingChanges
            if (renamingFile.category === 'MSDSDocuments') {
                const updated = localMsdsDocs.map((msds: any) =>
                    msds.filePath === renamingFile.originalName
                        ? { ...msds, filePath: newFileName.trim(), lastUpdatedUtc: new Date().toISOString() }
                        : msds
                );
                setLocalMsdsDocs(updated);
                if (mergePendingChanges) {
                    mergePendingChanges({ materialSafetyDataSheets: updated } as any);
                }
            } else if (renamingFile.category === 'ProductDocuments') {
                const updated = localProductDocs.map((doc: any) =>
                    doc.filePath === renamingFile.originalName
                        ? { ...doc, filePath: newFileName.trim(), lastUpdatedUtc: new Date().toISOString() }
                        : doc
                );
                setLocalProductDocs(updated);
                if (mergePendingChanges) {
                    mergePendingChanges({ productDocuments: updated } as any);
                }
            }

            handleCancelRename();
        } catch (err) {
            console.error('Failed to rename file:', err);
            alert('Failed to rename file. Please try again.');
        }
    }, [renamingFile, newFileName, renameFile, handleCancelRename, mergePendingChanges, localMsdsDocs, localProductDocs]);

    // IBC Specific Requirements that trigger auto-warnings (matching old AngularJS logic)
    const ibcRequirements = product.category?.regulations?.ibcRegulation?.specificAndOperationalRequirements || [];
    const ibcWarningMap: Record<string, string> = {
        'IBC-16.2.6': 'The IBC Specific Requirement 16.2.6 applies to this product.',
        'IBC-16.2.7': 'The IBC Specific Requirement 16.2.7 applies to this product.',
        'IBC-16.2.9': 'The IBC Specific Requirement 16.2.9 applies to this product.',
        'IBC-15.13': 'The IBC Specific Requirement 15.13 applies to this product.',
    };
    const ibcWarningLines = ibcRequirements
        .filter((req) => ibcWarningMap[req.paragraphNumber])
        .map((req) => ibcWarningMap[req.paragraphNumber]);

    // Warning is in comments[5] (manual warning text)
    const warningComment = product.comments?.find((comment) => comment.category === 5);
    const manualWarning = (warningComment?.text || '').trim();
    const hasManualWarning = manualWarning !== '' || formData.warning.trim() !== '';

    // Combined warning: IBC auto-warnings + manual warning comment
    const hasWarning = ibcWarningLines.length > 0 || hasManualWarning;
    const warningDisplayText = [...ibcWarningLines, manualWarning].filter((s) => s).join('\n');

    // Description is in comments[4]
    const descriptionComment = product.comments?.find((comment) => comment.category === 4);
    const description = descriptionComment?.text || 'No description available';

    // CAS Number from externalIds
    const casNumber = product.externalIds?.find((id) => id.source === 'CAS')?.value || 'N/A';

    return (
        <SectionCard>
            <SectionTitle>General Information</SectionTitle>
            <InfoGrid>
                <ProductNameField $variant={showEditUI && !formData.categoryName?.trim() ? 'required' : undefined}>
                    <label title="The official names that are listed in IBC code Chapter 17 and 18 or in the MEPC.2 / Circ list. These are the names to be used in the Shipping Documents for identification of the products.">
                        {formData.regulation === '2' || formData.regulation === '3'
                            ? 'Product Name (IBC):'
                            : formData.regulation === '4'
                                ? 'Product Name (IGC):'
                                : 'Product Name:'}
                        {showEditUI && <span style={{ color: '#e57373', marginLeft: 2 }}>*</span>}
                    </label>
                    {showEditUI && isProductCategory ? (
                        <EditableInput type="text" value={formData.categoryName} onChange={(e) => updateFormField('categoryName', e.target.value)} />
                    ) : (
                        <div>
                            {isCreatingNewProduct || relatedProducts.length === 0 ? (
                                // Show category name when creating a new product or related products haven't loaded
                                <span>{formatCategoryName(product.category?.name, product.tradeName, categoryContains) || 'N/A'}</span>
                            ) : (
                                (() => {
                                    // Show only category-level products (no trade name) — these are the base/default products.
                                    // Products with a tradeName but no charterer are trade-name copies and should not appear here.
                                    const categoryProducts = relatedProducts.filter(
                                        (rel) => !rel.tradeName || rel.tradeName.trim() === ''
                                    );

                                    if (categoryProducts.length === 0) {
                                        // No category products found, show category name
                                        return <span>{formatCategoryName(product.category?.name, product.tradeName, categoryContains) || 'N/A'}</span>;
                                    }

                                    return (
                                        <>
                                            {categoryProducts.map((rel) => {
                                                const isCurrentProduct = rel.id === product.id;
                                                const displayName = formatCategoryName(rel.categoryName, product.tradeName, rel.contains ?? categoryContains);

                                                if (isCurrentProduct || !hasEditorRole) {
                                                    return <div key={rel.id}>{displayName}</div>;
                                                } else {
                                                    return (
                                                        <div key={rel.id}>
                                                            <Link
                                                                to={`/product?productId=${rel.id}`}
                                                                title={`Synonyms: ${rel.synonyms || ''}`}
                                                                style={{ color: '#0066cc', textDecoration: 'underline' }}
                                                            >
                                                                {displayName}
                                                            </Link>
                                                        </div>
                                                    );
                                                }
                                            })}
                                        </>
                                    );
                                })()
                            )}
                        </div>
                    )}
                </ProductNameField>
                {!isProductCategory && (
                    <ProductNameField $variant={showEditUI && !formData.tradeName?.trim() ? 'required' : undefined}>
                        <label title="This is the name of product provided by the charterer">Trade Name:{showEditUI && <span style={{ color: '#e57373', marginLeft: 2 }}>*</span>}</label>
                        {showEditUI ? (
                            <EditableInput type="text" value={formData.tradeName} onChange={(e) => updateFormField('tradeName', e.target.value)} style={{ flex: 1 }} />
                        ) : (
                            <div>{product.tradeName || 'N/A'}</div>
                        )}
                    </ProductNameField>
                )}
                <InfoItem>
                    <label title="Product regulation: Annex I, Annex II or IGC">Regulation:</label>
                    {showEditUI && isCreatingNewCategory ? (
                        <EditableSelect
                            value={formData.regulation}
                            onChange={(e) => updateFormField('regulation', e.target.value)}
                        >
                            <option value="1">Annex I</option>
                            <option value="2">Annex II - Chapter 17</option>
                            <option value="3">Annex II - Chapter 18</option>
                        </EditableSelect>
                    ) : (
                        <span>{formatMarpolRegulation(product.category?.regulations?.marpolRegulation) || 'N/A'}</span>
                    )}
                </InfoItem>

                {!isProductCategory && (
                    <InfoItem variant={showEditUI && !formData.chartererName ? 'required' : undefined}>
                        <label title="This is the name of the charterer">Charterer:{showEditUI && <span style={{ color: '#e57373', marginLeft: 2 }}>*</span>}</label>
                        {showEditUI ? (
                            <EditableSelect
                                value={formData.chartererName}
                                onChange={(e) => updateFormField('chartererName', e.target.value)}
                            >
                                <option value="">-- Select Charterer --</option>
                                {charterers.map((c) => (
                                    <option key={c.id} value={c.id}>
                                        {c.name}
                                    </option>
                                ))}
                            </EditableSelect>
                        ) : (
                            <span>{(product.charterer?.id ? charterers.find((c) => c.id === product.charterer?.id)?.name : product.charterer?.name) || 'N/A'}</span>
                        )}
                    </InfoItem>
                )}
                {!isProductCategory && !isCreatingNewCategory && (
                    <InfoItem>
                        <label title="Chemical Abstracts Service registry number">CAS Number:</label>
                        <span>{casNumber}</span>
                    </InfoItem>
                )}

                {!isCreatingNewCategory && (product.regulations?.tripartiteAgreement || (showEditUI && isProductCategory && product.regulations)) && (
                    <InfoItem>
                        <label title='"Contains" information provided in the Tripartite Agreement'>Contains:</label>
                        {showEditUI && isProductCategory ? (
                            <EditableInput
                                type="text"
                                value={formData.contains}
                                onChange={(e) => updateFormField('contains', e.target.value)}
                            />
                        ) : (
                            <span>{categoryContains || 'N/A'}</span>
                        )}
                    </InfoItem>
                )}
            </InfoGrid>

            {/* Warning Box — show in edit mode (so warnings can be added) or when a warning exists */}
            {(showEditUI || hasWarning) && (
                <WarningBox>
                    <label title="Warning containing precautions against dangerous situations">Warning:</label>
                    {showEditUI ? (
                        <>
                            {ibcWarningLines.length > 0 && (
                                <pre style={{ marginBottom: '0.5rem' }}>{ibcWarningLines.join('\n')}</pre>
                            )}
                            <EditableTextarea
                                value={formData.warning}
                                onChange={(e) => updateFormField('warning', e.target.value)}
                                placeholder="Enter warning text..."
                            />
                        </>
                    ) : (
                        <pre>{warningDisplayText}</pre>
                    )}
                </WarningBox>
            )}

            {/* Full-width fields */}
            <FullWidthFields>
                <FullWidthItem>
                    <label title="The synonyms are a list of similar trade/shipping names for this product">Synonyms:</label>
                    {showEditUI ? (
                        <EditableInput
                            type="text"
                            value={formData.synonyms}
                            onChange={(e) => updateFormField('synonyms', e.target.value)}
                            placeholder="Enter synonyms separated by commas..."
                        />
                    ) : (
                        <div className="full-width-content">{getSynonymsString(product.synonyms) || 'N/A'}</div>
                    )}
                </FullWidthItem>
                <FullWidthItem>
                    <label title="Short description of the product">Description:</label>
                    {showEditUI ? (
                        <EditableTextarea
                            value={formData.description}
                            onChange={(e) => updateFormField('description', e.target.value)}
                            placeholder="Enter product description..."
                        />
                    ) : (
                        <div className="full-width-content">{description}</div>
                    )}
                </FullWidthItem>
                {/* Comments (MSG) - only visible to users who canEdit */}
                {canEdit && (showEditUI || formData.commentsMSG) && (
                    <FullWidthItem>
                        <label title="Internal comments only visible for editors">Comments (MSG):</label>
                        {showEditUI ? (
                            <EditableTextarea
                                value={formData.commentsMSG}
                                onChange={(e) => updateFormField('commentsMSG', e.target.value)}
                                placeholder="Enter MSG comments..."
                            />
                        ) : (
                            <pre style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{formData.commentsMSG}</pre>
                        )}
                    </FullWidthItem>
                )}
                <FullWidthItem>
                    <label title="Products with the same Product Category">Related Products/Trade Names:</label>
                    <ScrollableContent>
                        {relatedProducts.length > 0 ? (
                            (() => {
                                // Exclude products already shown in "Product Name (IBC)" (those without charterer)
                                const productsToShow = relatedProducts.filter((rel) => {
                                    const isCurrentProduct = rel.id === product.id;
                                    const hasCharterer = rel.chartererName && rel.chartererName.trim() !== '';
                                    // Show only products that have a charterer and are not the current product
                                    return !isCurrentProduct && hasCharterer;
                                });

                                if (productsToShow.length === 0) {
                                    return <span>No related products with charterers</span>;
                                }

                                return (
                                    <>
                                        {productsToShow.map((rel) => {
                                            const displayName = rel.tradeName || rel.categoryName;
                                            const isDefaultProduct = !rel.tradeName;
                                            return (
                                                <div key={rel.id}>
                                                    {isDefaultProduct && !hasEditorRole ? (
                                                        <span>
                                                            {displayName}
                                                            {rel.chartererName && ` - ${rel.chartererName}`}
                                                        </span>
                                                    ) : (
                                                        <Link
                                                            to={`/product?productId=${rel.id}`}
                                                            title={`Synonyms: ${rel.synonyms}`}
                                                            style={{ color: '#0066cc', textDecoration: 'underline' }}
                                                        >
                                                            {displayName}
                                                            {rel.chartererName && ` - ${rel.chartererName}`}
                                                        </Link>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </>
                                );
                            })()
                        ) : (
                            <span>Loading related products...</span>
                        )}
                    </ScrollableContent>
                </FullWidthItem>
            </FullWidthFields>

            {/* Documents Section */}
            <FullWidthFields>
                <FullWidthItem>
                    <DocumentSection>
                        {/* MSDS Documents */}
                        <DocumentSubSection>
                            <h3>Material Safety Data Sheets</h3>
                            {localMsdsDocs && localMsdsDocs.length > 0 ? (
                                <DocumentList>
                                    {localMsdsDocs.map((msds: any, index: number) => (
                                        <DocumentItem key={msds.id || `msds-${index}`}>
                                            {showEditUI && (
                                                <DeleteButton
                                                    onClick={() => handleDeleteFile('MSDSDocuments', msds.filePath)}
                                                    disabled={isDeleting}
                                                    title="Delete file"
                                                >
                                                    <FontAwesomeIcon icon={faTrash} />
                                                </DeleteButton>
                                            )}
                                            {showEditUI && renamingFile?.category !== 'MSDSDocuments' && (
                                                <EditButton
                                                    onClick={() => handleStartRename('MSDSDocuments', index, msds.filePath)}
                                                    disabled={isRenaming}
                                                    title="Rename file"
                                                >
                                                    <FontAwesomeIcon icon={faPen} />
                                                </EditButton>
                                            )}
                                            <label title="Material Safety Data Sheet for this product">MSDS Document {index + 1}:</label>
                                            {renamingFile?.category === 'MSDSDocuments' && renamingFile?.index === index ? (
                                                <>
                                                    <RenameInput
                                                        type="text"
                                                        value={newFileName}
                                                        onChange={(e) => setNewFileName(e.target.value)}
                                                        autoFocus
                                                    />
                                                    <RenameActions>
                                                        <ConfirmButton onClick={handleConfirmRename} disabled={isRenaming} title="Confirm rename">
                                                            <FontAwesomeIcon icon={faCheck} />
                                                        </ConfirmButton>
                                                        <CancelButton onClick={handleCancelRename} disabled={isRenaming} title="Cancel rename">
                                                            <FontAwesomeIcon icon={faTimes} />
                                                        </CancelButton>
                                                    </RenameActions>
                                                </>
                                            ) : (
                                                <a
                                                    href="#"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        downloadFile({ category: 'MSDSDocuments', fileName: msds.filePath });
                                                    }}
                                                >
                                                    {msds.filePath}
                                                </a>
                                            )}
                                        </DocumentItem>
                                    ))}
                                </DocumentList>
                            ) : (
                                <span>No MSDS documents available</span>
                            )}

                            {/* MSDS Upload Button */}
                            {showEditUI && (
                                <div>
                                    <UploadButton onClick={() => msdsFileInputRef.current?.click()} disabled={isUploading}>
                                        {isUploading ? 'Uploading...' : 'Upload New MSDS'}
                                    </UploadButton>
                                    <HiddenFileInput ref={msdsFileInputRef} type="file" onChange={handleMsdsFileUpload} />
                                </div>
                            )}
                        </DocumentSubSection>

                        {/* Product Documents */}
                        <DocumentSubSection>
                            <h3>Product Documents</h3>
                            {localProductDocs && localProductDocs.length > 0 ? (
                                <DocumentList>
                                    {localProductDocs.map((doc: any, index: number) => (
                                        <DocumentItem key={doc.id || `doc-${index}`}>
                                            {showEditUI && (
                                                <DeleteButton
                                                    onClick={() => handleDeleteFile('ProductDocuments', doc.filePath)}
                                                    disabled={isDeleting}
                                                    title="Delete file"
                                                >
                                                    <FontAwesomeIcon icon={faTrash} />
                                                </DeleteButton>
                                            )}
                                            {showEditUI && renamingFile?.category !== 'ProductDocuments' && (
                                                <EditButton
                                                    onClick={() => handleStartRename('ProductDocuments', index, doc.filePath)}
                                                    disabled={isRenaming}
                                                    title="Rename file"
                                                >
                                                    <FontAwesomeIcon icon={faPen} />
                                                </EditButton>
                                            )}
                                            <label title="Documents for this product">Product Document {index + 1}:</label>
                                            {renamingFile?.category === 'ProductDocuments' && renamingFile?.index === index ? (
                                                <>
                                                    <RenameInput
                                                        type="text"
                                                        value={newFileName}
                                                        onChange={(e) => setNewFileName(e.target.value)}
                                                        autoFocus
                                                    />
                                                    <RenameActions>
                                                        <ConfirmButton onClick={handleConfirmRename} disabled={isRenaming} title="Confirm rename">
                                                            <FontAwesomeIcon icon={faCheck} />
                                                        </ConfirmButton>
                                                        <CancelButton onClick={handleCancelRename} disabled={isRenaming} title="Cancel rename">
                                                            <FontAwesomeIcon icon={faTimes} />
                                                        </CancelButton>
                                                    </RenameActions>
                                                </>
                                            ) : (
                                                <a
                                                    href="#"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        downloadFile({ category: 'ProductDocuments', fileName: doc.filePath });
                                                    }}
                                                >
                                                    {doc.filePath}
                                                </a>
                                            )}
                                        </DocumentItem>
                                    ))}
                                </DocumentList>
                            ) : (
                                <span>No product documents available</span>
                            )}

                            {/* Product Document Upload Button */}
                            {showEditUI && (
                                <div>
                                    <UploadButton onClick={() => productDocFileInputRef.current?.click()} disabled={isUploading}>
                                        {isUploading ? 'Uploading...' : 'Upload New Product Document'}
                                    </UploadButton>
                                    <HiddenFileInput ref={productDocFileInputRef} type="file" onChange={handleProductDocFileUpload} />
                                </div>
                            )}
                        </DocumentSubSection>

                        {/* Legacy MSDS (single document) */}
                        {product.materialSafetyDataSheet && (!product.materialSafetyDataSheets || product.materialSafetyDataSheets.length === 0) && (
                            <DocumentSubSection>
                                <h3>Material Safety Data Sheet</h3>
                                <DocumentList>
                                    <DocumentItem>
                                        <label title="Material Safety Data Sheet for this product">MSDS Document:</label>
                                        <a
                                            href="#"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                downloadFile({ category: 'MSDS', fileName: product.materialSafetyDataSheet.filePath });
                                            }}
                                        >
                                            {product.materialSafetyDataSheet.filePath}
                                        </a>
                                    </DocumentItem>
                                </DocumentList>
                            </DocumentSubSection>
                        )}
                    </DocumentSection>
                </FullWidthItem>
            </FullWidthFields>
        </SectionCard>
    );
};

export default GeneralInformationSection;
